import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Store} from '@ngrx/store';
import {Subject, takeUntil} from 'rxjs';
import {FormControl, FormGroup} from '@angular/forms';
import {MatSort, Sort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import * as fromRoot from '../../../../../state/app.state';
import * as FoodGuideActions from '../../core/food-guide.actions';
import * as FoodGuideSelectors from '../../core/food-guide.selectors';
import {FoodGuideItem} from '../../model/food-guide.model';
import {CommonService} from '../../../../shared/services/common.service';
import {FoodGuideFormComponent} from '../../components/food-guide-form/food-guide-form.component';
import {FoodGuideAuditComponent} from '../../components/food-guide-audit/food-guide-audit.component';
import {ActivatedRoute} from '@angular/router';

@Component({
  selector: 'app-food-guide-list-screen',
  templateUrl: './food-guide-list-screen.component.html',
  styleUrls: ['./food-guide-list-screen.component.css']
})
export class FoodGuideListScreenComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort) sort: MatSort;

  private readonly onDestroy: Subject<any> = new Subject<any>();

  loading = false;
  retiring = false;
  isFirstPage = true;
  dataSource: MatTableDataSource<FoodGuideItem> = new MatTableDataSource();
  pageNumber = 1;
  limit = 20;
  totalItems = 0;
  hasNext = false;

  sortColumn = 'name';
  sortOrder = 'ASC';

  displayedColumns = ['id', 'name', 'color', 'category', 'servingSize', 'searchTerms', 'isActive', 'updatedAt', 'actionMenu'];

  colorOptions = [
    {key: 'Green', value: 'Green'},
    {key: 'Yellow', value: 'Yellow'},
    {key: 'Red', value: 'Red'},
  ];

  categoryOptions: {key: number, value: string}[] = [];
  categoryMap: {[key: number]: string} = {};

  statusOptions = [
    {key: 'true', value: 'Active'},
    {key: 'false', value: 'Retired'},
  ];

  colorIndicators: any = {
    'Green': '#28A745',
    'Yellow': '#FFC107',
    'Red': '#DC3545',
  };

  form = new FormGroup<any>({
    color: new FormControl({value: null, disabled: false}),
    category: new FormControl({value: null, disabled: false}),
    status: new FormControl({value: null, disabled: false}),
    search: new FormControl({value: null, disabled: false}),
  });

  errorMessages: any = {};
  errorMessageMap = {
    color: {},
    category: {},
    status: {},
    search: {},
  };

  constructor(
    private store: Store<fromRoot.State>,
    private commonService: CommonService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.store.select(FoodGuideSelectors.foodGuideList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.loading = data.loading;
        this.totalItems = data.total;
        this.hasNext = data.hasNext;

        if (data.pageNumber === 1) {
          this.dataSource.data = data.list || [];
        } else {
          this.dataSource.data = [...this.dataSource.data, ...(data.list || [])];
        }
      });

    this.store.select(FoodGuideSelectors.retiring)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(retiring => this.retiring = retiring);

      this.store.select(FoodGuideSelectors.foodCategories)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(categories => {
        this.categoryOptions = categories || [];
        this.categoryMap = {};
        for (const cat of this.categoryOptions) {
          this.categoryMap[cat.key] = cat.value;
        }
      });

    this.store.dispatch(FoodGuideActions.FetchFoodCategoriesRequest());


    this.commonService
      .onNavigationEnd
      .pipe(takeUntil(this.onDestroy))
      .subscribe(d => {
        const urlParams = {
          params: this.route.snapshot.params,
          queryParams: this.route.snapshot.queryParams
        }

        const prevUrlParams = this.commonService.urlParams.getValue();

        if (d.isRouteChanged || JSON.stringify(prevUrlParams) !== JSON.stringify(urlParams)) {
          this.commonService.urlParams.next(urlParams);
        }
      });

    this.fetchList();
  }

  fetchList(): void {
    const {color, category, status, search} = this.form.getRawValue();
    this.store.dispatch(FoodGuideActions.FetchFoodGuideListRequest({
      payload: {
        searchText: search || '',
        color: color || '',
        category: category || '',
        isActive: status || '',
        sortBy: this.sortColumn,
        sortOrder: this.sortOrder,
        pageNumber: this.pageNumber,
        limit: this.limit,
      }
    }));
  }

  apply(): void {
    this.pageNumber = 1;
    this.isFirstPage = true;
    this.form.markAsPristine();
    this.fetchList();
  }

  onScroll(): void {
    if (!this.loading && this.hasNext) {
      this.pageNumber++;
      this.isFirstPage = false;
      this.fetchList();
    }
  }

  sortData(sort: Sort): void {
    const sortFieldMap: any = {
      id: 'fgId',
      name: 'name',
      updatedAt: 'updatedAt',
    };
    this.sortColumn = sortFieldMap[sort.active] || 'name';
    this.sortOrder = sort.direction === 'desc' ? 'DESC' : 'ASC';
    this.pageNumber = 1;
    this.isFirstPage = true;
    this.fetchList();
  }

  openAddDialog(): void {
    const dialogRef = this.commonService.openDialog({
      data: {
        component: FoodGuideFormComponent,
        data: {mode: 'create'},
      },
      panelWidth: 50,
    });

    dialogRef?.afterClosed().subscribe(() => {
      this.pageNumber = 1;
      this.isFirstPage = true;
      this.fetchList();
    });
  }

  openEditDialog(item: FoodGuideItem): void {
    const dialogRef = this.commonService.openDialog({
      data: {
        component: FoodGuideFormComponent,
        data: {mode: 'edit', item},
      },
      panelWidth: 50,
    });

    dialogRef?.afterClosed().subscribe(() => {
      this.pageNumber = 1;
      this.isFirstPage = true;
      this.fetchList();
    });
  }

  openAuditDialog(item: FoodGuideItem): void {
    this.commonService.openDialog({
      data: {
        component: FoodGuideAuditComponent,
        data: {item},
      },
      panelWidth: 60,
    });
  }

  retireItem(item: FoodGuideItem): void {
    this.store.dispatch(FoodGuideActions.RetireFoodGuideItemRequest({
      payload: {id: item.id}
    }));

    this.store.select(FoodGuideSelectors.retiring)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(retiring => {
        if (!retiring) {
          this.pageNumber = 1;
          this.isFirstPage = true;
          this.fetchList();
        }
      });
  }

  unretireItem(item: FoodGuideItem): void {
    this.store.dispatch(FoodGuideActions.UnretireFoodGuideItemRequest({
      payload: {id: item.id}
    }));

    this.store.select(FoodGuideSelectors.retiring)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(retiring => {
        if (!retiring) {
          this.pageNumber = 1;
          this.isFirstPage = true;
          this.fetchList();
        }
      });
  }

  checkForErrors(currentField?: string): void {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField))
    };
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
    this.store.dispatch(FoodGuideActions.ClearState());
  }
}
